import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api, warrantyPillClass, warrantyLabel, type AssetEvent } from "../lib/api.ts";
import { useStaff } from "../lib/staff.ts";

// Staff asset / warranty + RMA console (COMMISSIONING_WARRANTY.md P5.2). Card =
// warranty + hardware identity + current device; the event timeline = full history;
// the admin actions wire the previously curl-only staff endpoints (rma-authorize,
// asset-serial-set, decommission).
export function StaffAssetDetailPage() {
  const { assetSerial } = useParams<{ assetSerial: string }>();
  const { role } = useStaff();
  const isAdmin = role === "admin";
  const qc = useQueryClient();
  const [actionErr, setActionErr] = useState<string | null>(null);

  const card = useQuery({
    queryKey: ["staff", "asset", assetSerial],
    queryFn: () => api.staffAssetCard(assetSerial!),
    enabled: !!assetSerial,
    retry: false,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["staff", "asset", assetSerial] });
  const onErr = (e: Error) => setActionErr(e.message);
  const dev = card.data?.device ?? null;

  const rma = useMutation({
    mutationFn: () => api.staffDeviceRmaAuthorize(dev!.orgId, dev!.id),
    onSuccess: () => { setActionErr(null); refresh(); }, onError: onErr,
  });
  const decom = useMutation({
    mutationFn: () => api.staffDeviceDecommission(dev!.orgId, dev!.id),
    onSuccess: () => { setActionErr(null); refresh(); }, onError: onErr,
  });
  const setSerial = useMutation({
    mutationFn: (s: string) => api.staffDeviceSetAssetSerial(dev!.orgId, dev!.id, s),
    onSuccess: () => { setActionErr(null); refresh(); }, onError: onErr,
  });
  const voidW = useMutation({
    mutationFn: (v: { void: boolean; reason?: string }) => api.staffVoidWarranty(assetSerial!, v),
    onSuccess: () => { setActionErr(null); refresh(); }, onError: onErr,
  });

  // Commissioning QA (P2.3/P2.5, staff-gated): readiness checks + finalize. Only
  // meaningful while the unit is still maker stock; once qa_passed exists the
  // panel just shows the pass.
  const qaPassedEvent = card.data?.events.find((e: AssetEvent) => e.type === "qa_passed") ?? null;
  const showQa = card.data?.asset.status === "in_stock";
  const qa = useQuery({
    queryKey: ["staff", "asset-qa", assetSerial],
    queryFn: () => api.staffAssetQaStatus(assetSerial!),
    enabled: !!assetSerial && showQa && !qaPassedEvent,
    retry: false,
  });
  const finalize = useMutation({
    mutationFn: (force: boolean) => api.staffAssetFinalize(assetSerial!, force ? { force: true } : {}),
    onSuccess: () => {
      setActionErr(null);
      qc.invalidateQueries({ queryKey: ["staff", "asset-qa", assetSerial] });
      refresh();
    },
    onError: onErr,
  });
  const serviceMode = useMutation({
    mutationFn: (enable: boolean) => api.staffDeviceServiceMode(dev!.orgId, dev!.id, enable),
    onSuccess: () => { setActionErr(null); refresh(); }, onError: onErr,
  });
  const repairNote = useMutation({
    mutationFn: (note: string) => api.staffAssetRepairNote(assetSerial!, note),
    onSuccess: () => { setActionErr(null); refresh(); }, onError: onErr,
  });
  const replace = useMutation({
    mutationFn: (replSerial: string) => api.staffAssetReplace(assetSerial!, replSerial),
    onSuccess: () => { setActionErr(null); refresh(); }, onError: onErr,
  });

  return (
    <>
      <Link to="/staff/assets">← Asset lookup</Link>
      <h1>{assetSerial}</h1>
      {card.isLoading && <p className="muted">Loading…</p>}
      {card.error && <p className="err">Couldn't load — {(card.error as Error).message}</p>}
      {actionErr && <p className="err">{actionErr}</p>}
      {card.data && (
        <>
          <div className="card">
            <div className="kv-grid">
              <span className="muted">Warranty</span>
              <span>
                <span className={warrantyPillClass(card.data.warranty.state)}>
                  {warrantyLabel(card.data.warranty.state)}
                </span>
                {card.data.warranty.expires_at
                  ? ` · expires ${new Date(card.data.warranty.expires_at).toLocaleDateString()}`
                  : ""}
              </span>
              <span className="muted">Status</span><span>{card.data.asset.status}</span>
              <span className="muted">Holder org</span><code className="code-xs">{card.data.asset.orgId}</code>
              <span className="muted">CM5 serial</span><code className="code-xs">{card.data.asset.serial ?? "—"}</code>
              <span className="muted">EK hash</span>
              <code className="code-xs">{card.data.asset.ekHash ? card.data.asset.ekHash.slice(0, 24) + "…" : "—"}</code>
              <span className="muted">Commissioned</span>
              <span>{card.data.asset.commissionedAt ? new Date(card.data.asset.commissionedAt).toLocaleString() : "—"}</span>
              <span className="muted">Sold</span>
              <span>
                {card.data.asset.soldAt ? new Date(card.data.asset.soldAt).toLocaleDateString() : "—"}
                {card.data.asset.soldToContact ? ` · ${card.data.asset.soldToContact}` : ""}
              </span>
              <span className="muted">Current device</span>
              {dev ? <code className="code-xs">{dev.id}</code> : <span className="muted">— (none paired)</span>}
            </div>
          </div>

          {isAdmin && (
            <div className="card">
              <h3>Actions</h3>
              <div className="row">
                {dev && (
                  <button
                    disabled={rma.isPending}
                    onClick={() => { if (confirm(`Authorise an RMA (board/TPM swap re-enroll) for ${dev.id}?`)) rma.mutate(); }}
                  >Authorise RMA</button>
                )}
                {dev && (
                  <button
                    className="secondary"
                    disabled={setSerial.isPending}
                    onClick={() => {
                      const s = window.prompt("Set / correct the asset serial for this device:", card.data!.asset.assetSerial);
                      if (s && s.trim()) setSerial.mutate(s.trim());
                    }}
                  >Set asset serial</button>
                )}
                {dev && (
                  <button
                    className="danger"
                    disabled={decom.isPending}
                    onClick={() => { if (confirm(`Decommission device ${dev.id}? Retires it + revokes its tokens.`)) decom.mutate(); }}
                  >Decommission</button>
                )}
                {dev && (
                  <button
                    className="secondary"
                    disabled={serviceMode.isPending}
                    title="RAM-only service unlock: the device opens DebugView/wizard until ITS next reboot; re-delivered on every refresh while enabled"
                    onClick={() => serviceMode.mutate(!(dev as { serviceModeAt?: string | null }).serviceModeAt)}
                  >{(dev as { serviceModeAt?: string | null }).serviceModeAt ? "Disable service mode" : "Enable service mode"}</button>
                )}
                {card.data.warranty.state === "void" ? (
                  <button
                    disabled={voidW.isPending}
                    onClick={() => { if (confirm("Restore this warranty?")) voidW.mutate({ void: false }); }}
                  >Restore warranty</button>
                ) : (
                  <button
                    className="danger"
                    disabled={voidW.isPending}
                    onClick={() => {
                      const r = window.prompt("Void this warranty — reason (optional):");
                      if (r !== null) voidW.mutate({ void: true, reason: r.trim() || undefined });
                    }}
                  >Void warranty</button>
                )}
                {card.data.asset.status !== "decommissioned" && (
                  <button
                    className="danger"
                    disabled={replace.isPending}
                    title="Beyond repair: a fresh in-stock, QA-passed unit takes over the remaining warranty"
                    onClick={() => {
                      const s = window.prompt(
                        "Warranty replacement — asset serial of the NEW unit (must be in stock + QA passed).\n" +
                        "This unit becomes decommissioned; the replacement inherits the sale + remaining warranty:");
                      if (s && s.trim()) {
                        if (confirm(`Replace ${assetSerial} with ${s.trim()}?`)) replace.mutate(s.trim());
                      }
                    }}
                  >Record replacement</button>
                )}
              </div>
            </div>
          )}

          {showQa && (
            <div className="card">
              <h3>Commissioning QA</h3>
              {qaPassedEvent ? (
                <p>
                  <span className="pill pass">passed</span>
                  {" "}on {new Date(qaPassedEvent.ts).toLocaleString()} — sale/transfer unlocked.
                </p>
              ) : (
                <>
                  {qa.isLoading && <p className="muted">Checking…</p>}
                  {qa.error && <p className="err">QA status — {(qa.error as Error).message}</p>}
                  {qa.data && (
                    <>
                      <div className="kv-grid">
                        <span className="muted">Paired for QA</span>
                        <span>{qa.data.paired ? "yes" : "no — pair the unit first"}</span>
                        <span className="muted">TPM enrolled</span><span>{qa.data.enrolled ? "✓" : "✗"}</span>
                        <span className="muted">Attested (24 h)</span><span>{qa.data.attested_fresh ? "✓" : "✗"}</span>
                        <span className="muted">Asset serial match</span><span>{qa.data.asset_serial_match ? "✓" : "✗"}</span>
                        <span className="muted">Results synced</span><span>{qa.data.results_synced ? "✓" : "✗"}</span>
                      </div>
                      {isAdmin && (
                        <div className="row" style={{ marginTop: "0.5rem" }}>
                          <button
                            disabled={finalize.isPending || !qa.data.ready}
                            title={qa.data.ready ? undefined : "All checks must pass (or use Force finalize)"}
                            onClick={() => {
                              if (confirm("Finalize commissioning QA? Writes qa_passed + registered, retires the QA pairing and unlocks sale/transfer."))
                                finalize.mutate(false);
                            }}
                          >{finalize.isPending ? "Finalizing…" : "Finalize QA"}</button>
                          {!qa.data.ready && qa.data.paired && (
                            <button
                              className="danger"
                              disabled={finalize.isPending}
                              onClick={() => {
                                if (confirm("FORCE finalize despite failed checks? The qa_passed event will record force:true."))
                                  finalize.mutate(true);
                              }}
                            >Force finalize</button>
                          )}
                        </div>
                      )}
                      <p className="muted" style={{ marginTop: "0.5rem" }}>
                        After finalize: delete local tests + Unpair on the device so it ships clean.
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          <div className="card">
            <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
              <h3>Event timeline</h3>
              <button
                className="secondary"
                disabled={repairNote.isPending}
                onClick={() => {
                  const n = window.prompt("Repair / service note for the timeline:");
                  if (n && n.trim()) repairNote.mutate(n.trim());
                }}
              >Add repair note</button>
            </div>
            {card.data.events.length === 0 ? (
              <p className="muted">No events.</p>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead><tr><th>When</th><th>Event</th><th>Actor</th><th>Detail</th></tr></thead>
                  <tbody>
                    {card.data.events.map((e: AssetEvent) => (
                      <tr key={e.id}>
                        <td className="muted">{new Date(e.ts).toLocaleString()}</td>
                        <td>{e.type}</td>
                        <td className="muted">{e.actor ?? "—"}</td>
                        <td><code className="code-xs muted">{e.detail ?? ""}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
